import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button as RawButton } from "@/components/ui/button";
import { CheckCircle2, XCircle, Info } from "lucide-react";

/**
 * @typedef {"success" | "error" | "info"} StatusDialogVariant
 *
 * @typedef {Object} StatusDialogProps
 * @property {boolean} open
 * @property {(open: boolean) => void} onOpenChange
 * @property {StatusDialogVariant} [variant] 
 * @property {string} title
 * @property {string} [description]
 * @property {string} [confirmLabel]
 * @property {() => void} [onConfirm]
 */

const VARIANT_STYLES = {
  success: {
    Icon: CheckCircle2,
    iconClass: "text-[#D4AF37]",
    ringClass: "bg-[#0B2421]",
  },
  error: {
    Icon: XCircle,
    iconClass: "text-red-600",
    ringClass: "bg-red-100",
  },
  info: {
    Icon: Info,
    iconClass: "text-[#0B2421]",
    ringClass: "bg-gray-100",
  },
};

export default function StatusDialog(/** @type {StatusDialogProps} */ {
  open,
  onOpenChange,
  variant = "info",
  title,
  description,
  confirmLabel = "OK",
  onConfirm,
}) {
  const Button = /** @type {React.ComponentType<any>} */ (RawButton);
  const { Icon, iconClass, ringClass } = VARIANT_STYLES[variant] || VARIANT_STYLES.info;

  const handleConfirm = () => {
    onConfirm?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className={`mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full ${ringClass}`}>
            <Icon className={`h-6 w-6 ${iconClass}`} />
          </div>
          <DialogTitle className="text-center">{title}</DialogTitle>
          {description ? (
            <DialogDescription className="text-center">{description}</DialogDescription>
          ) : null}
        </DialogHeader>
        <DialogFooter className="sm:justify-center">
          <Button onClick={handleConfirm} className="w-full sm:w-auto bg-[#0B2421] hover:bg-[#0B2421]/85 text-white">
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}